/**
 * 🔒 P0 FIX FINAL (Jan 2026): Hook partagé pour la progression globale
 * 
 * Ce hook est la SOURCE DE VÉRITÉ UNIQUE pour la progression globale.
 * Il est utilisé par TOUS les composants qui affichent la progression.
 * 
 * GARANTIES:
 * - Un seul appel API
 * - Un seul calcul
 * - Même valeur partout
 * - Cache localStorage partagé entre navigations
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

// Cache persistant dans localStorage pour éviter les appels multiples
// et garantir la même valeur sur toutes les pages
const CACHE_KEY = 'global_progress_cache';
const CACHE_DURATION = 10000; // 10 secondes

const getCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < CACHE_DURATION) {
        return data;
      }
    }
  } catch (e) {
    console.error('[useGlobalProgress] Cache read error:', e);
  }
  return null;
};

const setCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
  } catch (e) {
    console.error('[useGlobalProgress] Cache write error:', e);
  }
};

export const useGlobalProgress = () => {
  const cachedData = getCache();
  const [globalProgress, setGlobalProgress] = useState(cachedData?.global_progress_percentage ?? 0);
  const [validatedModules, setValidatedModules] = useState(cachedData?.validated_modules ?? 0);
  const [totalModules, setTotalModules] = useState(cachedData?.total_modules ?? 20);
  const [validatedObjectives, setValidatedObjectives] = useState(cachedData?.validated_objectives ?? 0);
  const [totalObjectives, setTotalObjectives] = useState(cachedData?.total_objectives ?? 40);
  const [modulesInProgress, setModulesInProgress] = useState(cachedData?.modules_in_progress ?? 0);
  const [totalEvidences, setTotalEvidences] = useState(cachedData?.total_evidences ?? 0);
  const [loading, setLoading] = useState(!cachedData);
  const [error, setError] = useState(null);

  const fetchProgress = useCallback(async (forceRefresh = false) => {
    // Utiliser le cache si disponible et pas expiré
    const cached = getCache();
    if (!forceRefresh && cached) {
      console.log('[useGlobalProgress] 📦 Using localStorage cache:', cached.global_progress_percentage);
      setGlobalProgress(cached.global_progress_percentage ?? 0);
      setValidatedModules(cached.validated_modules ?? 0);
      setTotalModules(cached.total_modules ?? 20);
      setValidatedObjectives(cached.validated_objectives ?? 0);
      setTotalObjectives(cached.total_objectives ?? 40);
      setModulesInProgress(cached.modules_in_progress ?? 0);
      setTotalEvidences(cached.total_evidences ?? 0);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const timestamp = Date.now();
      
      const response = await axios.get(`${API}/pedagogical/dashboard/summary?_t=${timestamp}`, {
        headers: token ? { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        } : {}
      });

      const data = response.data;
      
      // Sauvegarder dans localStorage
      setCache(data);
      
      // Mettre à jour le state local
      setGlobalProgress(data.global_progress_percentage ?? 0);
      setValidatedModules(data.validated_modules ?? 0);
      setTotalModules(data.total_modules ?? 20);
      setValidatedObjectives(data.validated_objectives ?? 0);
      setTotalObjectives(data.total_objectives ?? 40);
      setModulesInProgress(data.modules_in_progress ?? 0);
      setTotalEvidences(data.total_evidences ?? 0);
      
      console.log(`[useGlobalProgress] ✅ API: ${data.global_progress_percentage}% (saved to localStorage)`);
      setError(null);
    } catch (err) {
      console.error('[useGlobalProgress] ❌ Error:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger au montage
  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Fonction pour forcer le rafraîchissement
  const refresh = useCallback(() => {
    localStorage.removeItem(CACHE_KEY);
    fetchProgress(true);
  }, [fetchProgress]);

  return {
    globalProgress,
    validatedModules,
    totalModules,
    validatedObjectives,
    totalObjectives,
    modulesInProgress,
    totalEvidences,
    loading,
    error,
    refresh
  };
};

// Export pour invalider le cache depuis l'extérieur (après évaluation Owen)
export const invalidateProgressCache = () => {
  localStorage.removeItem(CACHE_KEY);
  console.log('[useGlobalProgress] 🗑️ Cache invalidated');
};

export default useGlobalProgress;
